import React, { useCallback, useEffect, useState } from 'react';
import SubsectionPanel from '../../common/SubsectionPanel/SubsectionPanel';
import InfoPanel from '../../common/InfoPanel/InfoPanel';
import GenericFlyoutPanel from '../../common/GenericFlyoutPanel/GenericFlyoutPanel';
import { ReferenceNumberConfigRestDataProvider } from '../../../data/ReferenceNumberConfigRestDataProvider';
import { ReferenceNumberConfig } from '../../../models/ReferenceNumberConfig';
import { useTranslation } from 'react-i18next';

const ReferenceNumbersTab: React.FC = () => {
    const { t } = useTranslation(['common', 'screens']);
    const [configs, setConfigs] = useState<ReferenceNumberConfig[]>([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [editItem, setEditItem] = useState<ReferenceNumberConfig | null>(null);
    const [flyoutOpen, setFlyoutOpen] = useState(false);

    const provider = React.useMemo(() => new ReferenceNumberConfigRestDataProvider(), []);

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const data = await provider.getAll();
            setConfigs(data);
        } catch (e: any) {
            setError(e.message);
        } finally {
            setLoading(false);
        }
    }, [provider]);

    useEffect(() => { loadData(); }, [loadData]);

    const openEdit = (config: ReferenceNumberConfig) => {
        setEditItem({ ...config });
        setFlyoutOpen(true);
    };

    const closeEdit = () => {
        setFlyoutOpen(false);
        setEditItem(null);
    };

    const handleSave = async () => {
        if (!editItem) return;
        try {
            await provider.update(editItem.id, editItem);
            setFlyoutOpen(false);
            setEditItem(null);
            await loadData();
        } catch (e: any) {
            setError(e.message);
        }
    };

    return (
        <div className="reference-numbers-tab">
            <SubsectionPanel title={t('screens:referenceNumbers.title')}>
                {error && <div style={{ color: 'red', marginBottom: 12 }}>{error}</div>}
                <InfoPanel header="About reference numbers">
                    Reference numbers are used to generate sequential identifiers for invoices, bank transactions, and other entities.
                    Each configuration defines the prefix, number of digits, and the starting value. Click a row to edit it.
                </InfoPanel>
                <table className="table table-hover">
                    <thead>
                        <tr>
                            <th>{t('screens:referenceNumbers.tableHeaders.key')}</th>
                            <th>{t('screens:referenceNumbers.tableHeaders.prefix')}</th>
                            <th>{t('screens:referenceNumbers.tableHeaders.digits')}</th>
                            <th>{t('screens:referenceNumbers.tableHeaders.startNumber')}</th>
                            <th>{t('screens:referenceNumbers.tableHeaders.currentNextNumber')}</th>
                        </tr>
                    </thead>
                    <tbody>
                        {configs.map(cfg => (
                            <tr key={cfg.id} style={{ cursor: 'pointer' }} onClick={() => openEdit(cfg)}>
                                <td>{cfg.configKey}</td>
                                <td>{cfg.prefix}</td>
                                <td>{cfg.digits}</td>
                                <td>{cfg.startNumber}</td>
                                <td>{cfg.currentNextNumber}</td>
                            </tr>
                        ))}
                    </tbody>
                </table>
                {loading && <div style={{ marginTop: 12 }}>{t('common:loading')}</div>}
            </SubsectionPanel>

            <GenericFlyoutPanel open={flyoutOpen} onClose={closeEdit} title={t('screens:referenceNumbers.editTitle')}>
                {editItem && (
                    <div className="p-3">
                        <div className="mb-3">
                            <label className="form-label">{t('screens:referenceNumbers.fields.configKey')}</label>
                            <input type="text" className="form-control" value={editItem.configKey} readOnly disabled />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">{t('screens:referenceNumbers.fields.prefix')}</label>
                            <input
                                type="text"
                                className="form-control"
                                value={editItem.prefix}
                                onChange={e => setEditItem({ ...editItem, prefix: e.target.value })}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">{t('screens:referenceNumbers.fields.digits')}</label>
                            <input
                                type="number"
                                className="form-control"
                                value={editItem.digits}
                                onChange={e => setEditItem({ ...editItem, digits: parseInt(e.target.value, 10) || 0 })}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">{t('screens:referenceNumbers.fields.startNumber')}</label>
                            <input
                                type="number"
                                className="form-control"
                                value={editItem.startNumber}
                                onChange={e => setEditItem({ ...editItem, startNumber: parseInt(e.target.value, 10) || 0 })}
                            />
                        </div>
                        <div className="mb-3">
                            <label className="form-label">{t('screens:referenceNumbers.fields.currentNextNumber')}</label>
                            <input type="number" className="form-control" value={editItem.currentNextNumber} readOnly disabled />
                        </div>
                        <div className="d-flex gap-2">
                            <button className="btn btn-primary" onClick={handleSave}>{t('common:save')}</button>
                            <button className="btn btn-secondary" onClick={closeEdit}>{t('common:cancel')}</button>
                        </div>
                    </div>
                )}
            </GenericFlyoutPanel>
        </div>
    );
};

export default ReferenceNumbersTab;
